import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { Button, DateRangePicker, SearchableInput, TextField } from 'foundations-library/components'
import { FiltersType } from '../util/types'
import { downloadCsv } from '../util/Api'
import saveCsvFile from '../util/downloadCsvFile'
import { LiveReceptionistUsageSummaryResponse } from 'interfaces'
import { defaultPresets } from 'foundations-library/src/components/DateRangePicker/dateRange'

const jss = () => ({
    filters: {
        margin: '20px 0',
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'center',
        gap: 10
    }
})

const useStyles = makeStyles(jss)

interface PropsInterface {
    onChange: (v: FiltersType) => unknown
    value: FiltersType | null
    minDate?: Date
    maxDate?: Date
    search?: string
    onSearchChange?: (value: string) => void
    scripts?: LiveReceptionistUsageSummaryResponse['scripts']
}

const commonProps = {
    labelPlacement: 'inside',
    autoWidth: true
}

/**
 *
 */
const Filters = ({
    onChange, value, minDate, maxDate, scripts, search = '', onSearchChange
}: PropsInterface): JSX.Element => {
    const styles = useStyles()
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState<boolean>(false)

    const downloadCsvFile = async () => {
        setError(null)
        setLoading(true)
        try {
            const data = await downloadCsv({
                start_date: value?.from,
                end_date: value?.to,
                scriptId: value?.scriptId,
                direction: value?.direction
            })
            saveCsvFile(data)
        } catch (err) {
            setError('Something went wrong')
        } finally {
            setLoading(false)
        }
    }

    return <div className={styles.filters}>
        <TextField
            placeholder='Search'
            aria-label='Search calls'
            leadingIcon='search'
            value={search}
            onChange={onSearchChange}
            onClear={() => onSearchChange?.('')}
        />
        <DateRangePicker
            presets={minDate && maxDate
                ? [{ label: 'This period', getRange: () => ({ from: minDate, to: maxDate }) }, ...defaultPresets]
                : defaultPresets}
            minDate={minDate}
            maxDate={maxDate}
            value={{
                from: value?.from != null ? new Date(value.from * 1000) : undefined,
                to: value?.to != null ? new Date(value.to * 1000) : undefined
            }}
            onChange={(v) => onChange({
                ...value,
                from: v?.from ? Math.floor(v.from.getTime() / 1000) : undefined,
                to: v?.to ? Math.floor(v.to.getTime() / 1000) : undefined
            })}
            label='Date range'
            {...commonProps}
        />
        <SearchableInput
            options={[
                { value: undefined, text: 'All' },
                { value: 'inbound', text: 'Inbound' },
                { value: 'outbound', text: 'Outbound' }
            ]}
            value={value?.direction}
            onChange={(v) => onChange({ ...value, direction: v })}
            label='Direction'
            {...commonProps}
        />
        {
            scripts?.length
                ? <SearchableInput
                    options={scripts.map((script) => ({
                        value: script.scriptId,
                        text: script.scriptName
                    }))}
                    label='Script'
                    value={value?.scriptId}
                    onChange={(v) => onChange({ ...value, scriptId: v })}
                    clearable={scripts.length > 1}
                    onClear={scripts.length > 1 ? () => onChange({ ...value, scriptId: undefined }) : undefined}
                    {...commonProps}
                />
                : <></>
        }
        <Button
            icon='download'
            iconLeft
            color='neutral'
            variant='outlined'
            onClick={downloadCsvFile}
            size='large'
            loading={loading}
        >Export CSV</Button>
        {error && <div role='alert'>{error}</div>}
    </div>
}

export default Filters
